import type { ApmBase } from '@elastic/apm-rum';
import appConfig from 'config';
import { isWindowAvailable } from 'constants/is-window-available';
import extendInstrumentation from './extent-instrumentation';

type ApmMode = 'error-only' | 'full-instrumentation';

let apmPromise: Promise<ApmBase | undefined> | null = null;

const baseApmConfig = {
  serviceName: appConfig?.applicationName,
  serverUrl: appConfig?.thirdParty?.apmRum?.url,
  serviceVersion: appConfig?.version,
  environment: appConfig?.env,
  transactionSampleRate: appConfig?.thirdParty?.apmRum?.transactionSampleRate,
  logLevel: appConfig?.thirdParty?.apmRum?.logLevel,
  instrument: false,
};

const apmInstrumentConfig = {
  disableInstrumentations: ['click'] as InstrumentationTypes[],
  breakdownMetrics: true,
  instrument: true,
};

/**
 * Initializes APM with the specified instrumentation mode.
 * @param instrument - Whether to enable Core Web Vitals instrumentation
 */
const initialiseApm = (instrument: boolean): Promise<ApmBase | undefined> => {
  apmPromise = import('@elastic/apm-rum')
    .then(({ init }) => {
      const agent = init({
        ...baseApmConfig,
        ...(instrument ? apmInstrumentConfig : undefined),
      });

      // Set window.apm so other MFEs/fragments can adopt it
      if (instrument && agent) {
        window.apm = extendInstrumentation(agent);
      }

      return agent;
    })
    .catch(e => {
      console.error('Failed to dynamically import APM:', e);
      apmPromise = null;
      return undefined;
    });

  return apmPromise;
};

/**
 * Get or initialise the APM agent.
 *
 * - **'error-only'**: For fragments - adopts existing `window.apm` or initialises without instrumentation
 * - **'full-instrumentation'**: For pages - enables Core Web Vitals tracking and sets `window.apm`
 *
 * @param mode - The APM mode: 'error-only' for fragments, 'full-instrumentation' for pages
 * @returns The APM agent instance, or undefined if unavailable (SSR) or initialisation failed
 */
export async function getApm(mode: ApmMode): Promise<ApmBase | undefined> {
  if (!isWindowAvailable) return undefined;
  if (apmPromise) return apmPromise;

  // Adopt existing APM from host page
  if (window.apm) {
    apmPromise = Promise.resolve(window.apm);
    return apmPromise;
  }

  return initialiseApm(mode === 'full-instrumentation');
}
